import React, { useCallback, useState } from 'react'
import styled from '@emotion/styled'
import {
  Table,
  Tag,
  DatePicker,
  Select,
  Space,
  Tooltip,
  Button,
  Modal,
  Form,
  Input,
  Checkbox,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { createSimpleStore } from 'react-simple-reducer'
import { useHistory } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { TurmaModel } from '../../models/TurmaModel'
import { ParceiroModel } from '../../models/ParceiroModel'
import { UF } from '../../models/CursoModel'
import api from '../../api'
import locale from 'antd/es/date-picker/locale/pt_BR'
import type { RangePickerProps } from 'antd/es/date-picker'

const { RangePicker } = DatePicker
const { Option } = Select

type TurmaStatus = 'open' | 'paid' | 'overdue'
type FiltroStatus = TurmaStatus | 'requested'

const initialState = {
  turmas: [] as TurmaModel[],
  total: 0,
  loading: false,
  pagination: {
    page: 1,
    limit: 15,
    offset: 0,
  },
  filters: {
    dateRange: null as [Date, Date] | null,
    status: null as FiltroStatus | null,
    parceiroId: null as number | null,
  },
  parceiros: [] as ParceiroModel[],
  statistics: {
    totalValorNewDriver: 0,
    totalTurmas: 0,
    totalMatriculas: 0,
  },
  modalOpen: false,
}

type TState = typeof initialState

const TurmasStore = createSimpleStore(
  initialState,
  {
    setTurmas: (state, { turmas, total, statistics }) => ({
      ...state,
      turmas,
      total,
      statistics: statistics || state.statistics,
      loading: false,
    }),
    setLoading: (state, loading) => ({
      ...state,
      loading,
    }),
    setPagination: (state, pagination) => ({
      ...state,
      pagination: { ...state.pagination, ...pagination },
    }),
    setFilters: (state, filters) => ({
      ...state,
      filters: { ...state.filters, ...filters },
    }),
    setParceiros: (state, parceiros) => ({
      ...state,
      parceiros,
    }),
    setModalOpen: (state, modalOpen: boolean) => ({
      ...state,
      modalOpen,
    }),
  },
  {
    thunks: {
      fetchTurmas(
        filters: Partial<TState['filters']>,
        paginationOverride?: Partial<TState['pagination']>
      ) {
        return async (dispatch, getState) => {
          const { pagination, filters: currentFilters } = getState()

          try {
            dispatch(TurmasStore.actions.setLoading(true))

            // Update filters only if new filters are provided
            if (Object.keys(filters).length > 0) {
              dispatch(TurmasStore.actions.setFilters(filters))
              dispatch(TurmasStore.actions.setPagination({ page: 1 }))
            }

            // Determine which pagination to use
            const effectivePagination = paginationOverride || pagination
            const effectiveFilters =
              Object.keys(filters).length > 0 ? { ...currentFilters, ...filters } : currentFilters

            // Build query parameters
            const queryParams: any = {
              page: effectivePagination.page,
              limit: effectivePagination.limit,
              orderBy: 'id',
              order: 'DESC',
            }

            // Add filters to query params
            if (effectiveFilters.dateRange) {
              const [startDate, endDate] = effectiveFilters.dateRange
              queryParams.startDate = startDate.toISOString()
              queryParams.endDate = endDate.toISOString()
            }

            if (effectiveFilters.status === 'requested') {
              queryParams.statusTurma = 'SOLICITADA'
            } else if (effectiveFilters.status) {
              queryParams.status = effectiveFilters.status
            }

            if (effectiveFilters.parceiroId) queryParams.parceiroId = effectiveFilters.parceiroId

            const response = await api.Turmas.listAll(queryParams)

            dispatch(
              TurmasStore.actions.setTurmas({
                turmas: response.rows,
                total: response.count,
                statistics: response.statistics,
              })
            )
          } catch (error) {
            console.error('Error fetching turmas:', error)
            dispatch(TurmasStore.actions.setLoading(false))
          }
        }
      },
      fetchParceiros() {
        return async (dispatch) => {
          try {
            const parceiros = await api.Parceiros.list()
            dispatch(TurmasStore.actions.setParceiros(parceiros))
          } catch (error) {
            console.error('Error fetching parceiros:', error)
          }
        }
      },
      openCriarTurmaModal() {
        return async (dispatch) => {
          dispatch(TurmasStore.actions.setModalOpen(true))
        }
      },
    },
  }
)

function getTurmaStatus(turma: TurmaModel): TurmaStatus {
  if (turma.pagamento?.dataPagamento) return 'paid'

  const today = new Date()
  const vencimento = new Date(turma.vencimento)

  if (vencimento < today) return 'overdue'

  return 'open'
}

const statusConfig = {
  paid: { color: 'green', text: 'Pago' },
  overdue: { color: 'red', text: 'Vencido' },
  open: { color: 'blue', text: 'Aberto' },
}

export const TurmasList = () => {
  const history = useHistory()

  const init = useCallback((dispatch) => {
    dispatch(TurmasStore.thunks.fetchTurmas({}))
    dispatch(TurmasStore.thunks.fetchParceiros())
  }, [])

  return (
    <TurmasStore.Provider init={init}>
      <Layout>
        <Container>
          <HeaderSection>
            <Title>Turmas</Title>
            <HeaderActions />
          </HeaderSection>
          <StatisticsSection />
          <FiltersSection />
          <TurmasTable />
          <CriarTurmaModal />
        </Container>
      </Layout>
    </TurmasStore.Provider>
  )
}

const UF_OPTIONS: UF[] = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]

const HeaderActions = () => {
  const dispatch = TurmasStore.useDispatch()

  return (
    <Button type="primary" onClick={() => dispatch(TurmasStore.thunks.openCriarTurmaModal())}>
      Nova Turma
    </Button>
  )
}

const CNPJ_NEW_DRIVER = '31.665.008/0001-09'

const criarTurmaSchema = Yup.object({
  descricaoTurma: Yup.string().required('Descrição é obrigatória'),
  uf: Yup.string().required('UF é obrigatória'),
  vencimento: Yup.string().nullable().required('Vencimento é obrigatório'),
  cpfCnpj: Yup.string().required('CPF/CNPJ é obrigatório'),
})

const CriarTurmaModal = () => {
  const history = useHistory()
  const dispatch = TurmasStore.useDispatch()
  const { modalOpen } = TurmasStore.useState()

  const initialValues = {
    descricaoTurma: '',
    uf: '' as UF | '',
    vencimento: null as string | null,
    cpfCnpj: '',
    permiteEmissaoCertificado: false,
  }

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }) => {
    try {
      const result = await api.Turmas.createOrUpdate({
        descricaoTurma: values.descricaoTurma,
        uf: values.uf,
        vencimento: values.vencimento,
        cpfCnpj: values.cpfCnpj,
        permiteEmissaoCertificado: values.permiteEmissaoCertificado,
      })
      dispatch(TurmasStore.actions.setModalOpen(false))
      history.push(`/turmas/${result.id}`)
    } catch (error) {
      message.error('Erro ao criar turma')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Nova Turma"
      open={modalOpen}
      onCancel={() => dispatch(TurmasStore.actions.setModalOpen(false))}
      footer={null}
      destroyOnClose
    >
      <Formik
        initialValues={initialValues}
        validationSchema={criarTurmaSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          isSubmitting,
        }) => (
          <Form layout="vertical" onFinish={formikSubmit}>
            <Form.Item
              label="Descrição da Turma"
              required
              validateStatus={touched.descricaoTurma && errors.descricaoTurma ? 'error' : ''}
              help={touched.descricaoTurma && errors.descricaoTurma}
            >
              <Input
                name="descricaoTurma"
                value={values.descricaoTurma}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Item>

            <Form.Item
              label="UF"
              required
              validateStatus={touched.uf && errors.uf ? 'error' : ''}
              help={touched.uf && errors.uf}
            >
              <Select
                value={values.uf || undefined}
                onChange={(value) => setFieldValue('uf', value)}
                onBlur={() => setFieldTouched('uf', true)}
                placeholder="Selecione a UF"
              >
                {UF_OPTIONS.map((uf) => (
                  <Option key={uf} value={uf}>
                    {uf}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Vencimento"
              required
              validateStatus={touched.vencimento && errors.vencimento ? 'error' : ''}
              help={touched.vencimento && errors.vencimento}
            >
              <DatePicker
                format="DD/MM/YYYY"
                locale={locale}
                style={{ width: '100%' }}
                onChange={(date) => {
                  setFieldValue('vencimento', date ? date.toISOString() : null)
                  setFieldTouched('vencimento', true)
                }}
              />
            </Form.Item>

            <Form.Item
              label="CPF/CNPJ Parceiro/Captador"
              required
              validateStatus={touched.cpfCnpj && errors.cpfCnpj ? 'error' : ''}
              help={touched.cpfCnpj && errors.cpfCnpj}
            >
              <Input
                name="cpfCnpj"
                value={values.cpfCnpj}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex: 00.000.000/0001-00"
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                    onClick={() => setFieldValue('cpfCnpj', CNPJ_NEW_DRIVER)}
                  >
                    New Driver
                  </Button>
                }
              />
            </Form.Item>

            <Form.Item>
              <Checkbox
                checked={values.permiteEmissaoCertificado}
                onChange={(event) =>
                  setFieldValue('permiteEmissaoCertificado', event.target.checked)
                }
              >
                Permite Emissão de Certificado
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={() => dispatch(TurmasStore.actions.setModalOpen(false))}>
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Criar Turma
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const StatisticsSection = () => {
  const { statistics } = TurmasStore.useState()

  return (
    <StatisticsContainer>
      <StatisticCard>
        <StatisticLabel>Valor New Driver</StatisticLabel>
        <StatisticValue>
          {new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(statistics.totalValorNewDriver)}
        </StatisticValue>
      </StatisticCard>

      <StatisticCard>
        <StatisticLabel>Turmas</StatisticLabel>
        <StatisticValue>{statistics.totalTurmas}</StatisticValue>
      </StatisticCard>

      <StatisticCard>
        <StatisticLabel>Matrículas</StatisticLabel>
        <StatisticValue>{statistics.totalMatriculas}</StatisticValue>
      </StatisticCard>
    </StatisticsContainer>
  )
}

const FiltersSection = () => {
  const dispatch = TurmasStore.useDispatch()
  const { filters, parceiros } = TurmasStore.useState()

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    dispatch(
      TurmasStore.thunks.fetchTurmas({
        dateRange: dates ? [dates[0].toDate(), dates[1].toDate()] : null,
      })
    )
  }

  const handleStatusChange = (value: FiltroStatus | undefined) => {
    dispatch(TurmasStore.thunks.fetchTurmas({ status: value || null }))
  }

  const handleParceiroChange = (value: number | undefined) => {
    dispatch(TurmasStore.thunks.fetchTurmas({ parceiroId: value || null }))
  }

  return (
    <FiltersContainer>
      <Space size="middle" wrap>
        <RangePicker
          locale={locale}
          format="DD/MM/YYYY"
          placeholder={['Data inicial', 'Data final']}
          onChange={handleDateRangeChange}
        />

        <Select
          placeholder="Status"
          allowClear
          style={{ width: 150 }}
          onChange={handleStatusChange}
          value={filters.status}
        >
          <Option value="requested">Solicitada</Option>
          <Option value="open">Aberto</Option>
          <Option value="paid">Pago</Option>
          <Option value="overdue">Vencido</Option>
        </Select>

        <Select
          placeholder="Parceiro"
          allowClear
          style={{ width: 200 }}
          onChange={handleParceiroChange}
          value={filters.parceiroId}
          showSearch
          optionFilterProp="children"
        >
          {parceiros.map((parceiro) => (
            <Option key={parceiro.id} value={parceiro.id}>
              {parceiro.nome}
            </Option>
          ))}
        </Select>

      </Space>
    </FiltersContainer>
  )
}

const TurmasTable = () => {
  const history = useHistory()
  const { turmas, loading, pagination, total } = TurmasStore.useState()
  const dispatch = TurmasStore.useDispatch()

  const columns: ColumnsType<TurmaModel> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: 'Descrição',
      dataIndex: 'descricaoTurma',
      ellipsis: true,
    },
    {
      title: 'Parceiro',
      key: 'parceiro',
      width: 200,
      render: (_, turma) => {
        const parceiroNome = turma.parceiro?.nome

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {parceiroNome && (
              <Tooltip title={parceiroNome}>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {parceiroNome}
                </span>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: 'Valor Total',
      dataIndex: ['pagamento', 'valor'],
      width: 120,
      render: (value) =>
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(value || 0),
    },
    {
      title: 'Valor New Driver',
      dataIndex: ['pagamento', 'valorNewDriver'],
      width: 140,
      render: (value) =>
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(value || 0),
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      width: 110,
      render: (value) => {
        if (!value) return '-'
        return new Date(value).toLocaleDateString('pt-BR')
      },
    },
    {
      title: 'Vencimento',
      dataIndex: 'vencimento',
      width: 110,
      render: (value) => {
        if (!value) return '-'
        return new Date(value).toLocaleDateString('pt-BR')
      },
    },
    {
      title: 'Pago em',
      dataIndex: 'dataPagamento',
      width: 110,
      render: (value) => {
        if (!value) return '-'
        return new Date(value).toLocaleDateString('pt-BR')
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, turma) => {
        if (turma.statusTurma === 'SOLICITADA') return <Tag color="orange">Solicitada</Tag>
        const status = getTurmaStatus(turma)
        const config = statusConfig[status]

        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    {
      title: 'Qtd Alunos',
      key: 'qtdAlunos',
      width: 100,
      align: 'center',
      render: (_, turma) => {
        return turma.matriculas?.length || 0
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, turma) => (
        <ViewButton onClick={() => history.push(`/turmas/${turma.id}`)}>Ver</ViewButton>
      ),
    },
  ]

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination
    const offset = (current - 1) * pageSize

    dispatch(
      TurmasStore.actions.setPagination({
        page: current,
        limit: pageSize,
        offset,
      })
    )
    dispatch(
      TurmasStore.thunks.fetchTurmas(
        {},
        {
          page: current,
          limit: pageSize,
          offset,
        }
      )
    )
  }

  return (
    <TableWrapper>
      <Table
        columns={columns}
        dataSource={turmas}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          pageSizeOptions: ['10', '15', '20', '50'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
    </TableWrapper>
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
  align-self: flex-start;
`

const StatisticsContainer = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const StatisticCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 120px;
  text-align: center;

  @media (max-width: 768px) {
    min-width: 100px;
    padding: 8px 12px;
  }
`

const StatisticLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
`

const StatisticValue = styled.div`
  font-size: 18px;
  color: #494d50;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const FiltersContainer = styled.div`
  padding: 20px;
  background: #ffffff;
  border-radius: 10px;
`

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 8px;
  width: 100%;
  overflow-x: auto;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }

  .ant-table-pagination {
    margin: 16px 0;
  }
`

const ViewButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    filter: brightness(0.9);
  }
`
